/**
 * /recognition — Revenue recognition schedules.
 *
 * Shows deferred revenue schedules per AR invoice / subscription.
 * Lists recognition entries with status (scheduled / posted).
 * Allows running recognition for all due entries.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse,
  IconButton, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { api } from "@/lib/api";
import { queryOptions } from "@tanstack/react-query";

export const Route = createFileRoute("/_auth/recognition")({
  component: RecognitionPage,
});

interface RecognitionEntry {
  id: string;
  period_no: number;
  recognize_on: string;
  amount: string;
  status: string;
  journal_id: string | null;
}

interface RecognitionSchedule {
  id: string;
  ar_invoice_id: string;
  subscription_id: string | null;
  customer_id: string;
  total_amount: string;
  currency_code: string;
  service_start: string;
  service_end: string;
  period_count: number;
  entries: RecognitionEntry[];
}

const recognitionQueryOptions = () =>
  queryOptions({
    queryKey: ["recognition-schedules"],
    queryFn: () => api<RecognitionSchedule[]>("/api/saas/recognition/schedules"),
  });

const ENTRY_COLOR: Record<string, "default" | "success" | "warning"> = {
  scheduled: "warning",
  posted: "success",
};

function fmtMoney(v: string | number, currency = "USD") {
  const n = typeof v === "string" ? parseFloat(v) : v;
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(n);
}

function ScheduleRow({ schedule }: { schedule: RecognitionSchedule }) {
  const [open, setOpen] = useState(false);
  const posted = schedule.entries.filter((e) => e.status === "posted").length;
  const total = schedule.entries.length;

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen((v) => !v)}>
            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
          {schedule.ar_invoice_id.slice(0, 8)}…
        </TableCell>
        <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>
          {schedule.service_start} → {schedule.service_end}
        </TableCell>
        <TableCell align="right" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
          {fmtMoney(schedule.total_amount, schedule.currency_code)}
        </TableCell>
        <TableCell align="center">{schedule.period_count}</TableCell>
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ flex: 1, bgcolor: "action.hover", borderRadius: 1, height: 6, overflow: "hidden" }}>
              <Box
                sx={{
                  width: `${total > 0 ? (posted / total) * 100 : 0}%`,
                  height: "100%",
                  bgcolor: posted === total ? "success.main" : "primary.main",
                  transition: "width 0.3s",
                }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">{posted}/{total}</Typography>
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ p: 0, borderBottom: open ? undefined : "none" }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ m: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}
                sx={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Recognition Entries
              </Typography>
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow sx={{ "& th": { fontWeight: 600, fontSize: 11 } }}>
                    <TableCell>Period</TableCell>
                    <TableCell>Recognize On</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Journal</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {schedule.entries.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell>{e.period_no}</TableCell>
                      <TableCell>{e.recognize_on}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: "monospace" }}>
                        {fmtMoney(e.amount, schedule.currency_code)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={e.status}
                          size="small"
                          color={ENTRY_COLOR[e.status] ?? "default"}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 11, color: "text.secondary" }}>
                        {e.journal_id ? e.journal_id.slice(0, 8) + "…" : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function RecognitionPage() {
  const qc = useQueryClient();
  const q = useQuery(recognitionQueryOptions());
  const [runResult, setRunResult] = useState<string | null>(null);

  const runMut = useMutation({
    mutationFn: () =>
      api<{ count: number }>("/api/saas/recognition/run", {
        method: "POST",
        body: JSON.stringify({ as_of: new Date().toISOString().slice(0, 10) }),
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["recognition-schedules"] });
      setRunResult(`${r.count} recognition journal(s) posted.`);
    },
  });

  const schedules = q.data ?? [];
  const totalScheduled = schedules.reduce(
    (s, sch) => s + sch.entries.filter((e) => e.status === "scheduled").length,
    0,
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TrendingUpIcon color="primary" />
          <Typography variant="h5" fontWeight={600}>Revenue Recognition</Typography>
          {totalScheduled > 0 && (
            <Chip label={`${totalScheduled} pending`} size="small" color="warning" />
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<PlayArrowIcon />}
          disabled={runMut.isPending}
          onClick={() => runMut.mutate()}
        >
          {runMut.isPending ? "Running…" : "Run Recognition"}
        </Button>
      </Box>

      {runResult && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRunResult(null)}>
          {runResult}
        </Alert>
      )}
      {runMut.error && (
        <Alert severity="error" sx={{ mb: 2 }}>{String(runMut.error)}</Alert>
      )}

      {q.isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em" } }}>
                <TableCell />
                <TableCell>Invoice</TableCell>
                <TableCell>Service Period</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="center">Periods</TableCell>
                <TableCell>Progress</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {schedules.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6, color: "text.secondary" }}>
                    No recognition schedules. They are created when deferred-revenue AR invoices are posted.
                  </TableCell>
                </TableRow>
              )}
              {schedules.map((s) => (
                <ScheduleRow key={s.id} schedule={s} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
